const PROOFREAD_SUFFIX_PATTERN = /(?:\s*\(Proofread\)\s*|-Proofread\s*)$/i
const ORIGINAL_SENTINELS = new Set(['original', 'proofread', 'original-proofread'])
const PAIR_DELIMITERS = ['->', '→', ' - ', ' to ', '-'] as const

const normalizeLangLabel = (value?: string | null): string =>
  (value ?? '').toString().replace(/0/g, ' ').trim()

const splitOnDelimiter = (value: string, delimiter: string): [string, string] | null => {
  const idx = value.indexOf(delimiter)
  if (idx < 0) return null
  return [value.slice(0, idx), value.slice(idx + delimiter.length)]
}

const extractRawPairValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value
  }
  if (value && typeof value === 'object' && 'value' in value) {
    const candidate = (value as { value?: unknown }).value
    if (typeof candidate === 'string') {
      return candidate
    }
  }
  return ''
}

export const stripProofreadLabel = (value: unknown): string =>
  extractRawPairValue(value).replace(PROOFREAD_SUFFIX_PATTERN, '').trim()

export const hasProofreadLabel = (value: unknown): boolean => {
  const raw = extractRawPairValue(value)
  return PROOFREAD_SUFFIX_PATTERN.test(raw)
}

export const isOriginalLanguageValue = (value: unknown): boolean => {
  const normalized = stripProofreadLabel(value).trim().toLowerCase()
  return ORIGINAL_SENTINELS.has(normalized)
}

export const splitLanguagePair = (pair: unknown): { sourceLang: string; targetLang: string } => {
  const trimmed = stripProofreadLabel(pair)
  if (!trimmed || isOriginalLanguageValue(trimmed)) {
    return { sourceLang: '', targetLang: '' }
  }

  for (const delimiter of PAIR_DELIMITERS) {
    const split = splitOnDelimiter(trimmed, delimiter)
    if (!split) continue
    return {
      sourceLang: normalizeLangLabel(split[0]),
      targetLang: normalizeLangLabel(split[1]),
    }
  }

  return { sourceLang: normalizeLangLabel(trimmed), targetLang: '' }
}

export const normalizeLanguagePairValue = (
  value: unknown,
  options?: { originalValue?: string },
): string => {
  const raw = extractRawPairValue(value)
  if (!raw.trim()) {
    return ''
  }
  if (isOriginalLanguageValue(raw)) {
    return options?.originalValue ?? 'Original'
  }
  const { sourceLang, targetLang } = splitLanguagePair(raw)
  if (sourceLang && targetLang) {
    return `${sourceLang}-${targetLang}`
  }
  return normalizeLangLabel(stripProofreadLabel(raw))
}

export const getTargetLanguageFromPair = (
  value: unknown,
  options?: { originalValue?: string },
): string => {
  const normalized = normalizeLanguagePairValue(value, options)
  if (!normalized || isOriginalLanguageValue(normalized)) {
    return ''
  }
  return splitLanguagePair(normalized).targetLang
}

export const formatLanguagePairDisplayText = (
  value: unknown,
  options?: { originalLabel?: string },
): string => {
  const raw = extractRawPairValue(value)
  const proofreadSuffix = hasProofreadLabel(raw) ? ' (Proofread)' : ''
  if (isOriginalLanguageValue(raw)) {
    return `${options?.originalLabel ?? 'Original'}${proofreadSuffix}`
  }

  const { sourceLang, targetLang } = splitLanguagePair(raw)
  if (sourceLang && targetLang) {
    return `${sourceLang} to ${targetLang}${proofreadSuffix}`
  }

  const fallback = normalizeLangLabel(stripProofreadLabel(raw))
  return `${fallback}${proofreadSuffix}`.trim()
}
